import React, { useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { X, ArrowRight } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { ActionButton, ConfirmButton } from 'components/ui/Button';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ReactComponent as TrainSVG } from 'assets/images/selectionTrain.svg';
import { colours } from 'themes/default';
import { ADS_THROUGH_BRUSSELS_UIC, ANY_BELGIAN_STATION_UIC, displaySeatsLeftBounds } from 'constants/booking';
import PassengerType from 'components/ui/PassengerType';
import { journeyFareShape, passengersShape } from 'constants/propTypes';
import classOfService from 'constants/propTypes/classOfService';

import {
  getLowestFarePrice,
  getNumberOfSeatsLeft,
  getPromoDiscount,
  isBetweenRange
} from '../JourneySearchTables/SearchResultsTable/SearchResultsTable.utils';
import { eurostarAndAddonLegFaresMatches } from '../JourneyDigest/JourneyDigest.utils';
import LoyaltyPointsInfos from '../LoyaltyPointsInfos';

import {
  buildFareName,
  buildFares,
  buildSelectionTabs,
  buildPaxInfosPerFareName,
  getInitialFare,
  getCurrentFares
} from './SelectFlexibility.utils';
import { isThroughFare, isSummatedFare } from './SelectFlexibility.utils';

import {
  ActionsContainer,
  BodyContainer,
  ContentContainer,
  Fieldset,
  FlexibilityContainer,
  FlexibilityInfoContainer,
  Footer,
  FooterContainer,
  Header,
  HeaderContainer,
  JourneyName,
  LabelContainer,
  LowestFareLabel,
  Passenger,
  PromoFareLabel,
  SeatsLeftLabel,
  SubtitleContainer,
  Tab,
  TabContainer,
  TabHeaderContainer,
  Text,
  TotalAction,
  FareContainer,
  ClassOfServiceBottomBorder
} from './SelectFlexibility.styles';
import { CLASSES_OF_SERVICE_BY_TYPE } from 'constants/classOfService';
import { brandCode } from 'constants/brandCodes';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';

const SelectFlexibility = ({
  selectedFare = {},
  selectedJourney = {},
  selectedOutbound = {},
  selectedInbound = {},
  selectFare,
  currency,
  isLoyaltyBooking,
  isWheelchairSearch,
  isEvacSearch,
  isUnassignedSearch,
  isGroupBooking,
  isExchange = false,
  isFlowForward = false,
  passengers,
  lowestFares = {},
  onClose,
  outboundInfo = {},
  inboundInfo = {},
  classesOfService = {},
  exchangedClassesOfService = {}
}) => {
  const [currentClass, setCurrentClass] = useState(null);
  const [currentFare, setCurrentFare] = useState(null);

  const currentDirection = selectedFare?.direction ?? 'outbound';
  const journeyInfo = currentDirection === 'outbound' ? outboundInfo : inboundInfo;
  const currentFareInfo = currentDirection === 'outbound' ? selectedOutbound : selectedInbound;

  const handleChangeClass = (selectedClass) => () => setCurrentClass(selectedClass);

  const handleSelectFare = () => {
    selectFare({ ...currentFare, direction: selectedFare?.direction });
    onClose();
  };

  const selectionTabs = useMemo(
    () => buildSelectionTabs(classesOfService[currentDirection], isWheelchairSearch, isEvacSearch, isUnassignedSearch) || [],
    [classesOfService, currentDirection, isWheelchairSearch, isEvacSearch, isUnassignedSearch]
  );

  const fareClasses = useMemo(() => buildFares(selectedJourney, passengers), [passengers, selectedJourney]);

  const currentFareClasses = useMemo(
    () =>
      getCurrentFares({
        currentClass,
        fareClasses,
        exchangedClassesOfService: exchangedClassesOfService[currentDirection]
      }),
    [currentClass, fareClasses, exchangedClassesOfService, currentDirection]
  );

  const activeFare = useMemo(
    () =>
      currentFareClasses.find(
        ({ name, paxInfo }) => buildFareName(name, paxInfo) === buildFareName(currentFare?.name, currentFare?.paxInfo)
      ),
    [currentFareClasses, currentFare]
  );

  useLayoutEffect(() => {
    if (selectedFare?.classOfService?.code) {
      setCurrentClass(selectedFare.classOfService.code);
    }
  }, [selectedFare]);

  useLayoutEffect(() => {
    if (currentFareClasses?.length > 0) {
      setCurrentFare(
        getInitialFare({
          currentFareClasses,
          currentFareInfo
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFareClasses]);

  const tabsBorderColor = selectionTabs.find(({ key }) => key === currentClass)?.backgroundColor || colours.Greyscale.MistGrey;

  const lowestFareForSelectedSections = Math.min(
    lowestFares?.[currentDirection]?.[
      CLASSES_OF_SERVICE_BY_TYPE.STANDARD.find((classOfService) => lowestFares?.[currentDirection]?.[classOfService])
    ],
    lowestFares?.[currentDirection]?.[
      CLASSES_OF_SERVICE_BY_TYPE.PLUS.find((classOfService) => lowestFares?.[currentDirection]?.[classOfService])
    ]
  );

  return (
    <FlexibilityContainer>
      <Header>
        <X size={28} onClick={onClose} style={{ cursor: 'pointer' }} />
        <Text width="100%" fontSize="20px" fontWeight={700} textAlign="center">
          <FormattedMessage id="selectFlexibility.selectType" />
        </Text>
      </Header>
      <ContentContainer>
        <SubtitleContainer data-cy="flexibility-title">
          <JourneyName>
            <TrainSVG width="70px" style={{ minWidth: '70px', marginRight: '8px' }} />
            <Text fontSize="14px" fontWeight={700}>
              {journeyInfo?.origin ?? ''}
              <ArrowRight size={16} color={colours.Shades.BlueShade40.toString()} style={{ margin: '0 8px -3px' }} />
              {journeyInfo?.destination || ''}
            </Text>
          </JourneyName>
          <Text fontSize="14px" fontWeight={700}>
            {selectedJourney?.timing?.date ? moment(selectedJourney.timing.date, 'YYYY-MM-DD').format('D MMM YYYY') : ''}{' '}
            <Text fontSize="14px">
              <FormattedMessage id="journeySummaryBox.at" /> {selectedJourney?.timing?.departs ?? ''}
            </Text>
          </Text>
        </SubtitleContainer>
        <TabContainer>
          <TabHeaderContainer borderColor={tabsBorderColor}>
            {selectionTabs.map(({ key, backgroundColor, text }) => {
              const fare = fareClasses.find((f) => f.classOfService.code === key);

              const isABSOrADS =
                [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(fare?.origin) ||
                [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(fare?.destination);

              const hasAvailableFares = fare?.bookingItems?.length > 0 && eurostarAndAddonLegFaresMatches(fare.bookingItems, isABSOrADS);

              const hasExistingTrain = hasAvailableFares && selectedJourney?.isExistingTrain && selectedJourney.isExistingTrain(fare, key);

              const disabled = !fare?.canBeBooked || !hasAvailableFares || selectedJourney?.isTimeRestricted || Boolean(hasExistingTrain);

              const lowestFare = getLowestFarePrice({
                fares: fareClasses.filter((f) => f.classOfService.code === key),
                isLoyalty: isLoyaltyBooking,
                isFlowForward,
                isGroup: isGroupBooking,
                isExchange,
                exchangedClassesOfService: exchangedClassesOfService[currentDirection]
              });

              return (
                <Tab
                  key={key}
                  data-cy={`flexibility-tab-${key}`}
                  data-testid={`flexibility-tab-${key}`}
                  isActive={key === currentClass}
                  backgroundColor={backgroundColor}
                  onClick={handleChangeClass(key)}
                  disabled={disabled}
                >
                  <Text fontSize="15px" fontWeight="700">
                    {text}
                  </Text>
                  {lowestFare !== undefined && lowestFare >= 0 && (
                    <Text fontSize="15px" fontWeight="700">
                      <FormattedMessage id="journeySummaryBox.from" />:{' '}
                      {isLoyaltyBooking ? (
                        <FormattedMessage id="searchResults.pts" values={{ points: lowestFare }} />
                      ) : (
                        <FormattedCurrency currency={currency} amount={lowestFare} />
                      )}
                    </Text>
                  )}
                </Tab>
              );
            })}
          </TabHeaderContainer>
          <Fieldset>
            {currentFareClasses.map((fare) => {
              const { name, paxInfo, totalPrice, totalLoyaltyPoints, legs } = fare;

              const fareName = buildFareName(name, paxInfo);

              const isActiveFare = fareName === buildFareName(currentFare?.name, currentFare?.paxInfo);

              const handleChangeFlexibilityLevel = () => !isActiveFare && setCurrentFare(fare);

              const productsPrice = totalPrice;

              const isLowestFare = isLoyaltyBooking
                ? Object.values(paxInfo).some(({ loyaltyPoints }) => loyaltyPoints === lowestFareForSelectedSections)
                : productsPrice === lowestFareForSelectedSections;

              const promoDiscount = getPromoDiscount(fare);

              const numberOfSeats = isGroupBooking ? 0 : getNumberOfSeatsLeft(fare);

              const seatsLeft = isBetweenRange(numberOfSeats, displaySeatsLeftBounds[0], displaySeatsLeftBounds[1]) ? numberOfSeats : 0;

              const paxInfosPerFareName = buildPaxInfosPerFareName(paxInfo, legs);

              const promoSectionFareName = Object.keys(paxInfosPerFareName).find((fareName) =>
                paxInfosPerFareName[fareName].some(({ serviceTypeCode }) => EUROSTAR_CARRIERS.includes(serviceTypeCode))
              );

              const productCodes = legs.flatMap((item) => item.products.map((item) => item.code));

              return (
                <FlexibilityInfoContainer
                  key={fareName}
                  data-cy={`flexibility-card-${name.split(' ').join('')}`}
                  data-testid={`flexibility-card-${name.split(' ').join('')}`}
                  isActive={isActiveFare}
                  onClick={handleChangeFlexibilityLevel}
                >
                  <>
                    {isSummatedFare(legs, productCodes) ? (
                      <FormattedMessage id="searchResults.summatedFare" />
                    ) : isThroughFare(legs, productCodes) ? (
                      <FormattedMessage id="searchResults.throughFare" />
                    ) : null}
                    {Object.entries(paxInfosPerFareName).map(([paxFareName, paxInfoList], index) => (
                      <FareContainer key={`${paxFareName}--${index}`}>
                        <HeaderContainer>
                          <Text fontSize="14px" fontWeight={700}>
                            {paxFareName}
                          </Text>
                          <LabelContainer>
                            {promoDiscount && paxFareName === promoSectionFareName && (
                              <PromoFareLabel data-cy={`flexibility-promo-fare-${name.split(' ').join('')}`}>
                                <FormattedMessage id="searchResults.promoDiscount" values={{ value: promoDiscount }} />
                              </PromoFareLabel>
                            )}
                            {isLowestFare && index === 0 && (
                              <LowestFareLabel data-cy={`flexibility-lowest-fare-${name.split(' ').join('')}`}>
                                <FormattedMessage id="searchResults.lowestFare" />
                              </LowestFareLabel>
                            )}
                            {seatsLeft > 0 && index === 0 && (
                              <SeatsLeftLabel data-cy={`flexibility-seats-left-${name.split(' ').join('')}`}>
                                <FormattedMessage id="searchResults.seatsLeft" values={{ seatsLeft }} />
                              </SeatsLeftLabel>
                            )}
                          </LabelContainer>
                        </HeaderContainer>

                        <BodyContainer>
                          {paxInfoList
                            ?.filter((paxInfoPerFareName) => paxInfoPerFareName.name === paxFareName)
                            ?.map((paxKey) => (
                              <Passenger key={paxKey.id} data-cy={`flexibility-passenger-${name.replace(/\s+/g, '')}-${paxKey.id}`}>
                                <Text fontSize="13px">
                                  {paxInfo[paxKey.id].count} x{' '}
                                  <PassengerType type={paxInfo[paxKey.id].type} disabilityType={paxInfo[paxKey.id].disabilityType} />
                                </Text>
                                <Text fontSize="13px">
                                  <FormattedCurrency currency={currency} amount={paxInfo[paxKey.id].price} />
                                  {!!paxInfo[paxKey.id].loyaltyPoints && (
                                    <LoyaltyPointsInfos
                                      dataId="pax-points-details"
                                      id="searchResults.pts"
                                      values={paxInfo[paxKey.id].loyaltyPoints}
                                    />
                                  )}
                                </Text>
                              </Passenger>
                            ))}
                        </BodyContainer>
                        <ClassOfServiceBottomBorder classOfServiceCode={paxInfoList[0]?.classOfServiceCode} />
                      </FareContainer>
                    ))}
                  </>

                  <FooterContainer data-cy="flexibility-total-selection">
                    <Text fontSize="13px" fontWeight="700">
                      <FormattedMessage id="summary.total" /> <FormattedCurrency currency={currency} amount={totalPrice} />
                      {!!totalLoyaltyPoints && (
                        <LoyaltyPointsInfos dataId="fare-total-points-details" id="searchResults.pts" values={totalLoyaltyPoints} />
                      )}
                    </Text>
                  </FooterContainer>
                </FlexibilityInfoContainer>
              );
            })}
          </Fieldset>
        </TabContainer>
      </ContentContainer>
      <Footer>
        <TotalAction data-cy="flexibility-total-journey">
          <Text fontSize="14px">
            <FormattedMessage id="selectFlexibility.journeyTotal" />
          </Text>
          <Text fontSize="14px" fontWeight={700}>
            <FormattedCurrency currency={currency} amount={activeFare?.totalPrice || 0} />
            {!!activeFare?.totalLoyaltyPoints && (
              <LoyaltyPointsInfos dataId="total-points-details" id="searchResults.pts" values={activeFare.totalLoyaltyPoints} />
            )}
          </Text>
        </TotalAction>
        <ActionsContainer>
          <ActionButton data-cy="cancel-selected-flexibility" onClick={onClose}>
            <FormattedMessage id="selectFlexibility.cancel" />
          </ActionButton>
          <ConfirmButton data-cy="confirm-selected-flexibility" onClick={handleSelectFare}>
            <FormattedMessage id="selectFlexibility.confirm" />
          </ConfirmButton>
        </ActionsContainer>
      </Footer>
    </FlexibilityContainer>
  );
};

SelectFlexibility.propTypes = {
  selectedOutbound: journeyFareShape,
  selectedInbound: journeyFareShape,
  selectedFare: journeyFareShape,
  selectedJourney: PropTypes.shape({
    timing: PropTypes.shape({
      arrives: PropTypes.string,
      date: PropTypes.string,
      departs: PropTypes.string,
      duration: PropTypes.number
    }),
    isTimeRestricted: PropTypes.bool,
    fares: PropTypes.arrayOf(journeyFareShape)
  }),
  selectFare: PropTypes.func,
  currency: PropTypes.string,
  isLoyaltyBooking: PropTypes.bool,
  isWheelchairSearch: PropTypes.bool,
  isEvacSearch: PropTypes.bool,
  isUnassignedSearch: PropTypes.bool,
  isGroupBooking: PropTypes.bool,
  isExchange: PropTypes.bool,
  isFlowForward: PropTypes.bool,
  passengers: passengersShape,
  lowestFares: PropTypes.shape({
    outbound: PropTypes.shape({
      SP: PropTypes.number,
      STD: PropTypes.number
    }),
    inbound: PropTypes.shape({
      SP: PropTypes.number,
      STD: PropTypes.number
    })
  }),
  onClose: PropTypes.func,
  outboundInfo: PropTypes.shape({
    date: PropTypes.string,
    origin: PropTypes.string,
    destination: PropTypes.string,
    originUIC: PropTypes.string,
    destinationUIC: PropTypes.string
  }),
  inboundInfo: PropTypes.shape({
    date: PropTypes.string,
    origin: PropTypes.string,
    destination: PropTypes.string,
    originUIC: PropTypes.string,
    destinationUIC: PropTypes.string
  }),
  classesOfService: PropTypes.shape({
    outbound: classOfService,
    inbound: classOfService
  }),
  exchangedClassesOfService: PropTypes.shape({
    outbound: PropTypes.shape({
      [brandCode.Lyria]: PropTypes.string,
      [brandCode.Eurostar]: PropTypes.string,
      [brandCode.SNCF]: PropTypes.string,
      [brandCode.INOUI]: PropTypes.string
    }),
    inbound: PropTypes.shape({
      [brandCode.Lyria]: PropTypes.string,
      [brandCode.Eurostar]: PropTypes.string,
      [brandCode.SNCF]: PropTypes.string,
      [brandCode.INOUI]: PropTypes.string
    })
  })
};

export default SelectFlexibility;
